import React, { useCallback, useEffect, useMemo, useState } from "react";
import styled from "styled-components";
import Selector from "../../components/selector";
import { useGrpcClient } from "../../lib/grpc";
import { PropertyAPIPromiseClient } from "../../gen/witbooking/backoffice/property/v1/property_api_grpc_web_pb";
import {
  CreatePropertyChainRequest,
  GetPropertyChildrenRequest,
} from "../../gen/witbooking/backoffice/property/v1/property_api_pb";
import { FullWidthLoader } from "../../components/full-width-loader";
import { SectionHeading } from "../../components/section-heading";
import { ACCESS_DENIED, getPropertyRpt } from "../../lib/auth";
import { useKeycloakAuthz } from "../../components/keycloak-provider";
import { Alert, Button, Card } from "react-bootstrap";
import { defaultFormMessages } from "../shared/crud/form";
import { defaultListMessages } from "../shared/crud/list";
import { defineMessages, FormattedMessage } from "react-intl";
import { useConfig } from "../../lib/config";
import { withAuthorizationVerifier } from "../../components/authorization-verifier";
import { useUserPropertyTickers } from "../../components/user-tickers-providers";
const messages = {
  ...defaultFormMessages,
  ...defaultListMessages,
  ...defineMessages({
    title: { id: "internalmenu.integrations.chain", defaultMessage: "Configure chains" },
    propertyTickerSelectPlaceholder: {
      id: "channelManager.placeholder.propertyTicker",
      defaultMessage: "Engine identifier",
    },
    childrenSelectPlaceholder: {
      id: "chain.placeholder.children",
      defaultMessage: "Children properties",
    },
  }),
};

const Container = styled.div`
  display: flex;
  flex-direction: column;
  margin: 1rem;
  > * {
    margin: 1rem 0;
  }
`;
const ButtonContainer = styled.div`
  display: flex;
  justify-content: flex-end;
  margin: 1rem;
`;

type Option = { value: string; label: string };

interface OperationError {
  message: string;
}

const Label = styled.div`
  font-size: 18px;
  margin-bottom: -10px;
`;

function ChainConfigurer() {
  const { engineBaseUrl } = useConfig();
  const userPropertyTickersList = useUserPropertyTickers();
  const grpcClient = useGrpcClient(PropertyAPIPromiseClient);

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<OperationError | null | undefined>(undefined);
  const [configSuccess, setConfigSuccess] = useState<boolean>(false);

  const [propertyTickers, setPropertyTickers] = useState<string[]>([]);
  const [children, setChildren] = useState<string[] | undefined>(undefined);
  const [chainOption, setChainOption] = useState<Option | null | undefined>(undefined);
  const [childrenOption, setChildrenOption] = useState<Option[]>([]);
  const keycloakAuthz = useKeycloakAuthz();

  useEffect(() => {
    const fetchPropertyTickers = async () => {
      setLoading(true);
      setPropertyTickers(userPropertyTickersList)
      setLoading(false);
    };
    fetchPropertyTickers();
  }, [userPropertyTickersList]);

  useEffect(() => {
    const fetchPropertyChildren = async () => {
      if (!chainOption || !chainOption.value) {
        return;
      }
      setLoading(true);
      setError(undefined);
      setConfigSuccess(false);
      try {
        const token = await getPropertyRpt(chainOption.value, keycloakAuthz);
        const tickersResponse = await grpcClient.getPropertyChildren(
          new GetPropertyChildrenRequest(),
          {
            Authorization: `Bearer ${token}`,
          }
        );
        setChildren(tickersResponse.getPropertyTickersList());
        setChildrenOption(
          tickersResponse.getPropertyTickersList().map(option => ({ value: option, label: option }))
        );
      } catch (e) {
        if (e === ACCESS_DENIED) {
          console.error("Access denied", e);
          setError({ message: "Access denied" });
        } else {
          console.error("Error retrieving children", e);
          setError({ message: "Error retrieving children" });
        }
      }
      setLoading(false);
    };
    fetchPropertyChildren();
  }, [chainOption, grpcClient, keycloakAuthz]);

  const handleSelectChainTicker = useCallback(selectedOption => setChainOption(selectedOption), [
    setChainOption,
  ]);

  const handleChangeChildren = useCallback(
    selectedOption => setChildrenOption(selectedOption || []),
    [setChildrenOption]
  );

  const configureChain = useCallback(async () => {
    if (!chainOption || !chainOption.value) {
      return;
    }
    setLoading(true);
    setError(undefined);
    setConfigSuccess(false);
    const token = await getPropertyRpt(chainOption.value, keycloakAuthz);
    const createPropertyChainRequest = new CreatePropertyChainRequest();
    createPropertyChainRequest.setChildrenList(childrenOption.map(it => it.value));
    try {
      await grpcClient.createPropertyChain(createPropertyChainRequest, {
        Authorization: `Bearer ${token}`,
      });
      setConfigSuccess(true);
    } catch (e) {
      setConfigSuccess(false);
      if (e === ACCESS_DENIED) {
        console.error("Access denied", e);
        setError({ message: "Access denied" });
      } else {
        console.error("Error creating chain", e);
        setError({ message: "Error creating chain" });
      }
    }
    setLoading(false);
  }, [chainOption, childrenOption, grpcClient, keycloakAuthz]);

  const options = useMemo(() => propertyTickers.map(option => ({ value: option, label: option })), [
    propertyTickers,
  ]);

  if (loading) {
    return <FullWidthLoader />;
  }

  return (
    <>
      <SectionHeading>
        <FormattedMessage {...messages.title} />
      </SectionHeading>
      <Card className="ml-4 mr-4 mb-4 mt-4">
        <Container>
          <Label>
            <FormattedMessage {...messages.propertyTickerSelectPlaceholder} />
          </Label>
          <Selector
            options={options}
            value={chainOption}
            onChange={handleSelectChainTicker}
            placeholder={<FormattedMessage {...messages.propertyTickerSelectPlaceholder} />}
          />
          {children && (
            <>
              <Label>
                <FormattedMessage {...messages.childrenSelectPlaceholder} />
              </Label>
              <Selector
                value={childrenOption}
                isMulti
                options={options}
                onChange={handleChangeChildren}
                placeholder={<FormattedMessage {...messages.childrenSelectPlaceholder} />}
              />
            </>
          )}
        </Container>
        {error && (
          <Alert variant="danger">
            <strong>
              <FormattedMessage {...messages.saveError} />
            </strong>
          </Alert>
        )}
        {configSuccess && (
          <Alert variant="success">
            <strong>
              <FormattedMessage {...messages.saveSuccess} />{" "}
              {chainOption && chainOption.value && (
                <a
                  target="_blank"
                  rel="noopener noreferrer"
                  href={`${engineBaseUrl}/chain/${chainOption.value}`}>
                  LINK
                </a>
              )}
            </strong>
          </Alert>
        )}
        <ButtonContainer>
          <Button variant="primary" type="button" onClick={configureChain}>
            <FormattedMessage {...messages.save} />
          </Button>
        </ButtonContainer>
      </Card>
    </>
  );
}

export default withAuthorizationVerifier(ChainConfigurer);
